import { useEffect, useId, useState } from 'react';
import { AlertTriangle, Check, ChevronDown, ChevronUp, Loader2, MapPin, Save, ScanSearch, ShieldCheck, X } from 'lucide-react';
import { communicationsRepository } from './api/communications';
import type { LookupSaveResult, LookupSearch, MessageEvent } from './domain';

const inProgress = new Set(['queued', 'running', 'partial']);

export function MessageLookup({ event, onSaved }: { event: MessageEvent; onSaved: () => void }) {
  const panelId = useId();
  const [open, setOpen] = useState(false);
  const [lookup, setLookup] = useState<LookupSearch | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveResult, setSaveResult] = useState<LookupSaveResult | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !lookup || !inProgress.has(lookup.status)) return;
    let cancelled = false;
    const timer = window.setInterval(async () => {
      try {
        const current = await communicationsRepository.getLookup(lookup.id);
        if (!cancelled) setLookup(current);
      } catch (reason) {
        if (!cancelled) setError(reason instanceof Error ? reason.message : 'Lookup status could not be refreshed.');
      }
    }, 1_000);
    return () => { cancelled = true; window.clearInterval(timer); };
  }, [open, lookup?.id, lookup?.status]);

  const toggle = async () => {
    if (open) { setOpen(false); return; }
    setOpen(true);
    if (lookup || loading) return;
    setLoading(true);
    setError('');
    try {
      const result = await communicationsRepository.createLookup({
        type: 'phone',
        query: event.contact.phoneE164 || event.contact.phoneNumber,
        businessId: event.businessId,
        contactId: event.contact.id,
        communicationId: event.id,
        communicationKind: 'message',
        strategy: 'deep',
      });
      setLookup(result);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'The caller lookup could not be started.');
    } finally {
      setLoading(false);
    }
  };

  const save = async () => {
    if (!lookup || saving || lookup.saved || inProgress.has(lookup.status)) return;
    setSaving(true);
    setError('');
    try {
      const result = await communicationsRepository.saveLookup(lookup.id);
      setSaveResult(result);
      setLookup(current => current ? { ...current, saved: true } : current);
      onSaved();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'The profile could not be saved.');
    } finally {
      setSaving(false);
    }
  };

  const candidate = lookup?.candidates[0];
  const running = loading || Boolean(lookup && inProgress.has(lookup.status));
  const sourceCount = candidate?.sources.length || 0;
  const address = candidate?.addresses.find(item => item.kind === 'likely_current') || candidate?.addresses[0];

  return <div className="message-lookup">
    <button type="button" className="lookup-contact-action" onClick={() => void toggle()} aria-expanded={open} aria-controls={panelId}>
      <ScanSearch size={18} /><span>Lookup caller</span><small>{open ? 'View results in this conversation' : 'Name, address & public sources'}</small>
      {open ? <ChevronUp className="message-lookup-chevron" size={16} /> : <ChevronDown className="message-lookup-chevron" size={16} />}
    </button>
    {open && <section id={panelId} className="message-lookup-panel" aria-label="Caller lookup results" aria-live="polite">
      <div className="message-lookup-heading"><strong>Caller lookup</strong><button type="button" onClick={() => setOpen(false)} aria-label="Close caller lookup"><X size={16} /></button></div>
      {running && <div className="message-lookup-progress"><Loader2 className="spinning" size={16} /><span>{lookup?.candidates.length ? 'Checking more sources…' : 'Checking LineWatch, Twenty and public sources…'}</span></div>}
      {error && <p className="message-lookup-error"><AlertTriangle size={15} />{error}</p>}
      {lookup?.status === 'failed' && <p className="message-lookup-error"><AlertTriangle size={15} />{lookup.error || 'Lookup failed.'}</p>}
      {candidate && <div className="message-lookup-identity">
        <div><small>Likely match</small><strong>{candidate.displayName || 'Name not verified'}</strong><span><ShieldCheck size={13} /> {candidate.confidenceLabel} · {Math.round(candidate.confidence)}%</span></div>
      </div>}
      {candidate && address && <p className="message-lookup-address"><MapPin size={14} /><span>{address.kind === 'likely_current' ? 'Likely address' : 'Possible address'}: {address.formatted}</span></p>}
      {candidate && <div className="message-lookup-demographics">
        {candidate.ageRange && <span>Age {candidate.ageRange}</span>}
        {!candidate.ageRange && candidate.approximateAge && <span>Approx. age {candidate.approximateAge}</span>}
        {candidate.gender && <span>{candidate.gender}</span>}
      </div>}
      {lookup?.status === 'complete' && !candidate && <p className="message-lookup-empty">No reliable identity was found. You can still add a name to this contact above.</p>}
      {lookup?.candidates && lookup.candidates.length > 1 && <p className="message-lookup-caution">{lookup.candidates.length} possible matches were kept separate. Confirm the person before saving.</p>}
      {lookup && (candidate || lookup.providerRuns.length > 0) && <details className="message-lookup-more">
        <summary>Phones, emails, properties & sources</summary>
        {candidate?.phones.map(phone => <p key={phone.numberE164}><strong>Phone</strong><span>{phone.formattedNumber || phone.numberE164}{phone.carrier ? ` · ${phone.carrier}` : ''}</span></p>)}
        {candidate?.emails.map(email => <p key={email.email}><strong>Email</strong><span>{email.email}</span></p>)}
        {candidate?.properties.map(property => <p key={property.parcelNumber || property.address}><strong>Property</strong><span>{property.address}</span></p>)}
        {candidate?.sources.map((source, index) => <p key={`${source.source}-${index}`}><strong>Source</strong><span>{source.source}</span></p>)}
        {lookup.providerRuns.map(run => <p key={run.id}><strong>{run.provider}</strong><span>{run.status}</span></p>)}
      </details>}
      {lookup && <div className="message-lookup-footer">
        <span>{sourceCount} source{sourceCount === 1 ? '' : 's'} · {lookup.providerRuns.filter(run => run.status === 'failed').length} unavailable</span>
        {candidate && <button type="button" onClick={() => void save()} disabled={saving || running || lookup.saved || lookup.candidates.length > 1}>
          {saving ? <Loader2 className="spinning" size={14} /> : lookup.saved ? <Check size={14} /> : <Save size={14} />}
          {lookup.saved ? 'Saved' : 'Save profile'}
        </button>}
      </div>}
      {saveResult && <p className="message-lookup-saved"><Check size={14} />{saveResult.propagation.linewatch.status === 'updated' ? 'Contact updated in LineWatch' : 'Profile saved'} · {saveResult.propagation.businessCrm.status === 'synced' ? 'CRM synced' : saveResult.propagation.businessCrm.status === 'queued' ? 'CRM sync queued' : 'Check CRM sync status'}</p>}
    </section>}
  </div>;
}
